import {
	AfterContentInit,
	ContentChildren,
	Directive,
	Input,
	OnDestroy,
	OnInit,
	QueryList,
} from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { Subscription } from 'rxjs';
import { NavigationHistoryService } from '../services/navigation-history.service';

@Directive({ selector: '[appQueryStringTabGroup]' })
export class QueryStringTabGroupDirective
	implements OnInit, AfterContentInit, OnDestroy
{
	/** Query string key used for this tab group. Defaults to `tabIndex`. */
	@Input() queryParamKey = 'tabIndex';
	/** Opt in to URL/browser history updates; existing consumers keep legacy behavior. */
	@Input() syncUrl = false;

	/** Projected tabs on this group (not nested groups inside matTabContent). */
	@ContentChildren(MatTab) tabs!: QueryList<MatTab>;

	tabIndex = 0;

	activeUrl: UrlSegment[] = [];

	tabSubscription?: Subscription;
	indexChangeSubscription?: Subscription;
	urlSubscription?: Subscription;
	tabsChangeSubscription?: Subscription;

	private queryIndex = 0;
	/** True only when the URL actually has this group's query key. */
	private hasQueryIndex = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly tabGroup: MatTabGroup,
		private readonly navHistory: NavigationHistoryService
	) {}

	ngOnInit(): void {
		this.urlSubscription = this.route.url.subscribe((url) => {
			this.activeUrl = url;
		});

		this.indexChangeSubscription = this.tabGroup.selectedIndexChange.subscribe(
			(selectedTabIndex: number) => {
				if (this.syncUrl && selectedTabIndex === this.normalizedIndex()) {
					return;
				}
				this.tabIndex = selectedTabIndex;
				this.queryIndex = selectedTabIndex;
				const queryParams = {
					...this.route.snapshot.queryParams,
					[this.queryParamKey]: selectedTabIndex,
				};
				this.navHistory.addRoute({
					url: this.activeUrl,
					queryParams,
				});
				if (!this.syncUrl) return;
				void this.router.navigate([], {
					relativeTo: this.route,
					queryParams: { [this.queryParamKey]: selectedTabIndex },
					queryParamsHandling: 'merge',
				});
			}
		);

		this.tabSubscription = this.route.queryParamMap.subscribe((param) => {
			const raw = param.get(this.queryParamKey);
			if (raw == null) {
				// First load with no key leaves [selectedIndex] alone. Property
				// details uses that binding for openTab=clients, and a late Clients
				// tab must not be snapped back to Details. Removing a key that was
				// present (browser Back) still returns to the first tab.
				if (!this.hasQueryIndex) return;
				this.hasQueryIndex = false;
				this.queryIndex = 0;
				this.tabIndex = 0;
				this.tabGroup.selectedIndex = 0;
				return;
			}
			const value = Number(raw);
			this.hasQueryIndex = true;
			this.queryIndex = Number.isInteger(value) && value >= 0 ? value : 0;
			this.applyQueryIndex();
		});
	}

	ngAfterContentInit(): void {
		// The tab count is only available after projected tabs initialize.
		this.applyQueryIndex();
		// Tabs rendered after async data loads arrive later; MatTabGroup clamps to 0 until then.
		this.tabsChangeSubscription = this.tabs.changes.subscribe(() =>
			this.applyQueryIndex()
		);
	}

	private normalizedIndex(): number {
		const count = this.tabs?.length;
		return count && this.queryIndex >= count ? 0 : this.queryIndex;
	}

	private applyQueryIndex(): void {
		if (!this.hasQueryIndex) return;
		this.tabIndex = this.normalizedIndex();
		this.tabGroup.selectedIndex = this.tabIndex;
	}

	ngOnDestroy(): void {
		this.tabSubscription?.unsubscribe();
		this.tabSubscription = undefined;

		this.indexChangeSubscription?.unsubscribe();
		this.indexChangeSubscription = undefined;

		this.urlSubscription?.unsubscribe();
		this.urlSubscription = undefined;

		this.tabsChangeSubscription?.unsubscribe();
		this.tabsChangeSubscription = undefined;
	}
}
